import React from 'react'
import { Select, Typography } from 'antd'
import { LineChartOutlined, BarChartOutlined } from '@ant-design/icons'
import ChartViewer from '../ReportingComponents/ChartViewer'
import { CompanyType } from '../../../__generated__/graphql'

import dayjs from 'dayjs'
import { trackEvent } from '../../../utils/analytics'
import { useOutletContext } from 'react-router-dom'
import { useCubeQuery } from '../../../utils/hooks/useCubeQuery'

const { Title, Text } = Typography

// Map frontend metric names to cube metric names
const mapMetricToCube = (metric: string) => {
  const metricMap: { [key: string]: string } = {
    'tdClicks': 'tdClicks',
    'tdImpressions': 'tdImpressions', 
    'reads': 'reads',
    'clickOuts': 'clickouts', // Note: different name in cube
    'conversions': 'conversions',
    'publisherRevenue': 'publisherRevenue',
    'totalCost': 'totalCost'
  }
  return metricMap[metric] || metric
}

// Real time series chart data using cube query
const useTimeSeriesData = (selectedMetric: string, dateRange: any, cubejsApi: any, campaignId: string, aggPublisherFilters: any[]) => {
  const cubeMetric = mapMetricToCube(selectedMetric)
  
  const { resultSet: timeSeriesData } = useCubeQuery(
    {
      dimensions: ["aggregateTdLevel.campaignName"],
      measures: [`aggregateTdLevel.${cubeMetric}`],
      order: {
        "aggregateTdLevel.date": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.campaignId",
          operator: "equals" as const,
          values: [campaignId],
        },
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals" as const,
          values: ["true"],
        },
        ...aggPublisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          granularity: "day",
          dateRange: dateRange,
        },
      ],
    },
    { cubejsApi: cubejsApi as any }
  )

  if (!timeSeriesData) {
    return {
      chartPivot: () => [],
      series: () => []
    }
  }

  return {
    chartPivot: () =>
      (timeSeriesData as any).tablePivot().map((row: any) => ({
        x: row["aggregateTdLevel.date.day"],
        y: row[`aggregateTdLevel.${cubeMetric}`] || 0,
      })),
    series: () => [
      {
        title: `${selectedMetric} Over Time`,
        series:
          (timeSeriesData as any).tablePivot().map((row: any) => ({
            value: row[`aggregateTdLevel.${cubeMetric}`] || 0,
          })),
      },
    ],
  }
}

// Get available metrics that have actual non-zero values
const getAvailableMetrics = (teaserData: any[], companyType: string) => {
  const baseMetrics = [
    { label: 'Reads', value: 'reads' },
    { label: 'Clicks', value: 'tdClicks' },
    { label: 'Impressions', value: 'tdImpressions' }
  ]

  // Check totals for each metric to see if they have meaningful data
  const availableMetrics = baseMetrics.filter(metric => {
    const total = teaserData.reduce((sum, teaser) => {
      const value = teaser[metric.value] || 0
      return sum + (typeof value === 'number' ? value : 0)
    }, 0)
    return total > 0
  })

  // Add company-specific metrics only if they have data
  const publisherRevenue = teaserData.reduce((sum, teaser) => sum + (teaser.publisherRevenue || 0), 0)
  const totalCost = teaserData.reduce((sum, teaser) => sum + (teaser.totalCost || 0), 0)
  const conversions = teaserData.reduce((sum, teaser) => sum + (teaser.conversions || 0), 0)

  if (companyType === CompanyType.Publisher && publisherRevenue > 0) {
    availableMetrics.push({ label: 'Revenue', value: 'publisherRevenue' })
  } else if (companyType !== CompanyType.Publisher && totalCost > 0) {
    availableMetrics.push({ label: 'Total Cost', value: 'totalCost' })
  }

  if (conversions > 0) {
    availableMetrics.push({ label: 'Conversions', value: 'conversions' })
  }

  return availableMetrics
}


interface AnalyticsDashboardProps {
  chartType: string
  onChartTypeChange: (type: string) => void
  selectedMetric: string
  onSelectedMetricChange: (metric: string) => void
  companyType: string
  dateRange?: any
  cubejsApi?: any
  campaignId?: string
  aggPublisherFilters?: any[]
  channelData?: any[]
  teaserData?: any[]
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  chartType,
  onChartTypeChange,
  selectedMetric,
  onSelectedMetricChange,
  companyType,
  dateRange,
  cubejsApi,
  campaignId,
  aggPublisherFilters = [],
  channelData: realChannelData,
  teaserData: realTeaserData
}) => {
  const { campaign } = useOutletContext() as { campaign: any }

  // Use real data only - no fallback to mock data
  const finalChannelData = realChannelData || []
  const finalTeaserData = realTeaserData || []

  const chartData = useTimeSeriesData(selectedMetric, dateRange, cubejsApi, campaignId || '', aggPublisherFilters)

  // Track chart type changes
  const handleChartTypeChange = (type: string) => {
    onChartTypeChange(type)
    trackEvent('report_chart_type_changed', {
      campaignId: campaign?._id,
      previousType: chartType,
      newType: type,
    })
  }

  // Track metric changes
  const handleMetricChange = (metric: string) => {
    onSelectedMetricChange(metric)
    trackEvent('report_metric_selected', {
      campaignId: campaign?._id,
      previousMetric: selectedMetric,
      newMetric: metric,
    })
  }

  const chartOptions = [
    { label: 'Line Chart', value: 'line', icon: LineChartOutlined },
    { label: 'Bar Chart', value: 'bar', icon: BarChartOutlined },
    { label: 'Area Chart', value: 'area', icon: LineChartOutlined }
  ]

  // Only show metrics that have actual data
  const metricOptions = getAvailableMetrics(finalTeaserData, companyType)
  
  // Ensure selectedMetric is valid, if not, use the first available metric
  const validSelectedMetric = metricOptions.find(m => m.value === selectedMetric)?.value || metricOptions[0]?.value || 'reads'
  
  // Auto-correct the selected metric if it's invalid
  React.useEffect(() => {
    if (metricOptions.length > 0 && !metricOptions.find(m => m.value === selectedMetric)) {
      onSelectedMetricChange(metricOptions[0].value)
    }
  }, [metricOptions, selectedMetric, onSelectedMetricChange])

  // Handle case where no metrics are available
  if (metricOptions.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center text-gray-500">
              <div className="text-xl mb-2">No Performance Data Available</div>
              <div className="text-sm">This campaign doesn't have any measurable performance metrics yet.</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Chart Controls */}
      <div className="flex flex-wrap items-center gap-6 p-5 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3">
          <Text strong className="text-sm font-medium text-gray-700">Chart Type:</Text>
          <Select
            value={chartType}
            onChange={handleChartTypeChange}
            className="w-36 [&_.ant-select-selector]:!border-gray-300 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!shadow-sm hover:[&_.ant-select-selector]:!border-blue-400 focus:[&_.ant-select-selector]:!border-blue-500 focus:[&_.ant-select-selector]:!ring-2 focus:[&_.ant-select-selector]:!ring-blue-100"
            size="middle"
            options={chartOptions}
          />
        </div>
        <div className="flex items-center gap-3">
          <Text strong className="text-sm font-medium text-gray-700">Metric:</Text>
          <Select
            value={validSelectedMetric}
            onChange={handleMetricChange}
            className="w-40 [&_.ant-select-selector]:!border-gray-300 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!shadow-sm hover:[&_.ant-select-selector]:!border-blue-400 focus:[&_.ant-select-selector]:!border-blue-500 focus:[&_.ant-select-selector]:!ring-2 focus:[&_.ant-select-selector]:!ring-blue-100"
            size="middle"
            options={metricOptions}
          />
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <Title level={4} className="!mb-1">
              {metricOptions.find(m => m.value === validSelectedMetric)?.label} Over Time
            </Title>
            <Text type="secondary" className="text-sm">
              {metricOptions.find(m => m.value === validSelectedMetric)?.label} development during selected time period
            </Text>
          </div>
        </div>
        <div className="h-96">
          {(() => {
            const hasChartData = chartData.chartPivot().length > 0
            
            if (!hasChartData) {
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <div className="text-xl mb-2">No Time Series Data Available</div>
                    <div className="text-sm">No data available for the selected time period and metric.</div>
                  </div>
                </div>
              )
            }
            
            return (
              <ChartViewer 
                resultSet={chartData} 
                pivotConfig={{
                  x: ['x'],
                  y: ['y']
                }}
                chartType={chartType}
                height="384px"
              />
            )
          })()}
        </div>
      </div>

      {/* Performance Breakdown */}
      {finalChannelData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <Title level={4} className="!mb-4">Top Performing Channels</Title>
            <div className="space-y-3">
              {finalChannelData.slice(0, 3).map((channel) => (
                <div key={channel.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Text strong>{channel.channel}</Text>
                    <div className="text-sm text-gray-500">{channel.clickOuts || 0} click outs</div>
                  </div>
                  <div className="text-right">
                    <Text strong className="text-blue-600">{channel.reads || 0} reads</Text>
                    <div className="text-sm text-gray-500">{channel.conversions || 0} conversions</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <Title level={4} className="!mb-4">Channel Distribution</Title>
            <div className="space-y-3">
              {finalChannelData.map((channel) => {
                const totalReads = finalChannelData.reduce((sum, c) => sum + (c.reads || 0), 0)
                const percentage = totalReads > 0 ? ((channel.reads || 0) / totalReads * 100).toFixed(1) : '0.0'
                return (
                  <div key={channel.key} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <Text>{channel.channel}</Text>
                      <Text>{percentage}%</Text>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-gray-500">
              <div className="text-lg mb-2">No Channel Data Available</div>
              <div className="text-sm">No traffic from other channels detected for this campaign.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalyticsDashboard